import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DogSchool } from '@/data/dogSchools';
import { getSupabase } from '@/integrations/supabase/client';
import { mapSupabaseRowToDogSchool } from '@/utils/supabaseMappers';
import { Star, MapPin, ArrowRight } from 'lucide-react';
import { OptimizedImage } from '@/components/OptimizedImage';

interface RatgeberCTAProps {
  cityKeywords?: string[];
  title?: string;
}

export const RatgeberCTA = ({ cityKeywords = [], title }: RatgeberCTAProps) => {
  const [topSchools, setTopSchools] = useState<DogSchool[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopSchools = async () => {
      setLoading(true);
      try {
        const supabase = getSupabase();
        let query = supabase
          .from('hundeschuleList')
          .select('*')
          .not('reviewsrating', 'is', null)
          .order('reviewsrating', { ascending: false })
          .limit(3);

        // Filter by city keywords if provided
        if (cityKeywords.length > 0) {
          // Try to match any of the city keywords
          const cityFilter = cityKeywords.map(city => `city.ilike.%${city}%`).join(',');
          query = query.or(cityFilter);
        }

        const { data, error } = await query;

        if (error) throw error;

        const mappedSchools = (data || []).map(mapSupabaseRowToDogSchool);
        setTopSchools(mappedSchools.slice(0, 3));
      } catch (error) {
        console.error('Error fetching top schools:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTopSchools();
  }, [cityKeywords]);

  if (loading || topSchools.length === 0) return null;

  const cityName = cityKeywords[0] || 'Ihrer Nähe';

  return (
    <Card className="my-8 border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
      <CardHeader>
        <CardTitle className="text-2xl">
          {title || `Top Hundeschulen in ${cityName}`}
        </CardTitle>
        <p className="text-gray-600">
          Diese Hundeschulen werden von Hundebesitzern besonders empfohlen:
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-3">
          {topSchools.map((school) => (
            <Link
              key={school.id}
              to={`/hundeschule/${school.city.toLowerCase().replace(/\s+/g, '-')}/${school.id}`}
              className="group"
            >
              <Card className="h-full transition-all hover:shadow-lg hover:scale-[1.02]">
                <div className="relative h-40 overflow-hidden rounded-t-lg">
                  <OptimizedImage
                    src={school.imageUrl || ''}
                    alt={school.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <Badge className="absolute top-2 right-2 bg-yellow-400 text-yellow-900">
                    <Star className="h-3 w-3 mr-1 fill-current" />
                    {school.googleReview}
                  </Badge>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-bold mb-2 group-hover:text-primary transition-colors">
                    {school.name}
                  </h3>
                  <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                    <MapPin className="h-4 w-4" />
                    <span>{school.city}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    {school.reviewCount} Bewertungen
                  </p>
                  <div className="flex items-center text-primary font-semibold text-sm">
                    Mehr erfahren
                    <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
        
        <div className="mt-6 text-center">
          <Button asChild size="lg">
            <Link to="/">
              Alle Hundeschulen durchsuchen
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
